// src/components/Airdrop.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Users,
  Plus,
  Trash2,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  Loader2,
  Copy,
  Info,
  Zap,
  DollarSign,
  Shield,
  Clock,
  Activity,
  Wallet,
  RefreshCw
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
  LAMPORTS_PER_SOL,
  ComputeBudgetProgram
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAccount,
  getMint,
  TOKEN_2022_PROGRAM_ID
} from '@solana/spl-token';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import solanaService from '../services/solana.service';

interface AirdropRecipient {
  id: string;
  address: string;
  amount: number;
  percentage?: number;
  status: 'pending' | 'checking' | 'ready' | 'sent' | 'failed';
  error?: string;
  hasTokenAccount?: boolean;
}

interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  decimals: number;
  supply: number;
  hasLiquidity: boolean;
  liquiditySOL?: number;
  pricePerToken?: number;
  userBalance?: number;
  userBalanceFormatted?: string;
  logoURI?: string;
  description?: string;
  isToken2022?: boolean;
}

interface TokenMetadata {
  symbol: string;
  name: string;
  logoURI?: string;
  description?: string;
}

const Airdrop: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const connection = useRef(new Connection(SOLANA_RPC_ENDPOINT, 'confirmed')).current;
  const liquidityCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const tokenLoadDebounce = useRef<NodeJS.Timeout | null>(null);

  // State
  const [tokenCA, setTokenCA] = useState('');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [recipients, setRecipients] = useState<AirdropRecipient[]>([
    { id: '1', address: '', amount: 0, status: 'pending' }
  ]);
  const [loading, setLoading] = useState(false);
  const [checkingLiquidity, setCheckingLiquidity] = useState(false);
  const [sending, setSending] = useState(false);
  const [txSignature, setTxSignature] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [inputMode, setInputMode] = useState<'amount' | 'percentage'>('amount');
  
  // Advanced settings - optimized for single transaction
  const [settings, setSettings] = useState({
    waitForLiquidity: false,
    minLiquiditySOL: 1,
    priorityFee: 0, // Set to 0 to avoid priority fees
    createTokenAccounts: true,
    batchSize: 25, // Increased to fit more in one transaction
    useJitoBundle: false,
    jitoTip: 0.001,
    singleTransaction: true // New option to force single transaction
  });

  useEffect(() => {
  const validRecipients = recipients.filter(r => r.amount > 0); // Remove address check
  const total = validRecipients.reduce((sum, r) => sum + (r.amount || 0), 0);
  setTotalAmount(total);
  
  // More accurate cost calculation
  const accountCreations = recipients.filter(r => r.address && !r.hasTokenAccount).length;
  const baseTxFee = 0.000005 * Math.ceil(validRecipients.length / settings.batchSize);
  const accountCreationCost = accountCreations * 0.00203928;
  const priorityFee = settings.priorityFee;
  const jitoFee = settings.useJitoBundle ? settings.jitoTip : 0;
  
  const totalCost = baseTxFee + accountCreationCost + priorityFee + jitoFee;
  setEstimatedCost(totalCost);
}, [recipients, settings, tokenInfo]); // Add tokenInfo to dependencies

  // Auto-load token data when CA changes
  useEffect(() => {
    if (tokenLoadDebounce.current) {
      clearTimeout(tokenLoadDebounce.current);
    }
    
    // Reset states
    setIsValidAddress(false);
    setAddressError('');
    setTokenInfo(null);
    
    if (tokenCA.length >= 32) {
      tokenLoadDebounce.current = setTimeout(() => {
        validateAndLoadToken();
      }, 500); // Debounce for 500ms
    }
    
    return () => {
      if (tokenLoadDebounce.current) {
        clearTimeout(tokenLoadDebounce.current);
      }
    };
  }, [tokenCA]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (liquidityCheckInterval.current) {
        clearInterval(liquidityCheckInterval.current);
      }
    };
  }, []);

  // Fetch token metadata from various sources
  const fetchTokenMetadata = async (mintAddress: string): Promise<TokenMetadata | null> => {
    try {
      // Use your backend API which handles multiple sources including RevShare
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      
      console.log('Fetching token metadata from backend:', mintAddress);
      const response = await fetch(`${API_BASE}/token-metadata/${mintAddress}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Token metadata received:', data);
        
        return {
          symbol: data.symbol || 'UNKNOWN',
          name: data.name || 'Unknown Token',
          logoURI: data.logoURI || data.logo || data.image || undefined,
          description: data.description || undefined
        };
      }
    } catch (error) {
      console.error('Failed to fetch token metadata from backend:', error);
    }

    // Fallback to basic metadata
    return {
      symbol: 'UNKNOWN',
      name: 'Unknown Token',
      logoURI: undefined,
      description: undefined
    };
  };

  // Check liquidity using backend API or Jupiter price
  const checkLiquidity = async (mintAddress: string): Promise<{
    hasLiquidity: boolean;
    liquiditySOL: number;
    pricePerToken: number;
  }> => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      
      // Use backend quote-price endpoint which handles Token-2022 properly
      const response = await fetch(`${API_BASE}/quote-price/${mintAddress}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.price && data.price > 0) {
          return {
            hasLiquidity: true,
            liquiditySOL: 0, // Backend doesn't provide liquidity amount
            pricePerToken: data.price
          };
        }
      }
    } catch (error) {
      console.error('Error checking liquidity via backend:', error);
    }
    
    // No liquidity found
    return {
      hasLiquidity: false,
      liquiditySOL: 0,
      pricePerToken: 0
    };
  };

  // Get user's token balance with proper program detection
  const getUserTokenBalance = async (
    mintPubkey: PublicKey, 
    userPubkey: PublicKey, 
    decimals: number,
    isToken2022: boolean
  ): Promise<{ balance: number; formatted: string }> => {
    try {
      const programId = isToken2022 ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
      const ata = await getAssociatedTokenAddress(
        mintPubkey, 
        userPubkey, 
        false, 
        programId
      );
      
      try {
        const account = await getAccount(connection, ata, 'confirmed', programId);
        const balance = Number(account.amount) / Math.pow(10, decimals);
        return {
          balance,
          formatted: balance.toLocaleString(undefined, { maximumFractionDigits: 4 })
        };
      } catch {
        // No token account found
        return { balance: 0, formatted: '0' };
      }
    } catch (error) {
      console.error('Error fetching token balance:', error);
      return { balance: 0, formatted: '0' };
    }
  };

  const validateAndLoadToken = async () => {
    if (!tokenCA || tokenCA.length < 32) {
      setAddressError('Please enter a valid token address');
      return false;
    }

    setLoading(true);
    setAddressError('');

    try {
      const mintPubkey = new PublicKey(tokenCA);
      
      // Check if the address is valid
      const accountInfo = await connection.getAccountInfo(mintPubkey);
      if (!accountInfo) {
        throw new Error('Token mint not found on chain');
      }

      // Replace lines 290-300 with this:
let mintInfo;
let isToken2022 = false;

try {
  // Try Token-2022 first (since your token appears to be Token-2022)
  console.log('Attempting to read as Token-2022...');
  mintInfo = await getMint(connection, mintPubkey, 'confirmed', TOKEN_2022_PROGRAM_ID);
  isToken2022 = true;
  console.log('Successfully read as Token-2022 program token');
} catch (e) {
  console.log('Token-2022 failed, trying regular Token program...');
  try {
    // Fallback to regular Token program
    mintInfo = await getMint(connection, mintPubkey, 'confirmed', TOKEN_PROGRAM_ID);
    isToken2022 = false;
    console.log('Successfully read as regular Token program token');
  } catch (e2) {
    console.error('Both program attempts failed:', e2);
    throw new Error('Unable to read token mint - ensure this is a valid SPL token address');
  }
}

      // Fetch metadata
      const metadata = await fetchTokenMetadata(tokenCA);

      // Get user balance if wallet is connected
      let userBalance = 0;
      let userBalanceFormatted = '0';
      if (publicKey) {
        const userPubkey = new PublicKey(publicKey);
        const balanceInfo = await getUserTokenBalance(
          mintPubkey, 
          userPubkey, 
          mintInfo.decimals,
          isToken2022
        );
        userBalance = balanceInfo.balance;
        userBalanceFormatted = balanceInfo.formatted;
      }

      // Calculate supply
      const supply = Number(mintInfo.supply) / Math.pow(10, mintInfo.decimals);

      // Get liquidity info immediately
      const liquidityData = await checkLiquidity(tokenCA);

      setTokenInfo({
        mint: tokenCA,
        symbol: metadata?.symbol || 'UNKNOWN',
        name: metadata?.name || 'Unknown Token',
        decimals: mintInfo.decimals,
        supply: supply,
        hasLiquidity: liquidityData.hasLiquidity,
        liquiditySOL: liquidityData.liquiditySOL,
        pricePerToken: liquidityData.pricePerToken,
        userBalance,
        userBalanceFormatted,
        logoURI: metadata?.logoURI,
        description: metadata?.description,
        isToken2022
      });

      setIsValidAddress(true);

      // Only start checking for liquidity if it doesn't exist and we want to wait
      if (settings.waitForLiquidity && !liquidityData.hasLiquidity) {
        startLiquidityCheck(mintPubkey);
      } else if (liquidityData.hasLiquidity || !settings.waitForLiquidity) {
        await checkRecipientAccounts();
      }

      return true;
    } catch (error: any) {
      console.error('Invalid token address:', error);
      setAddressError(error.message || 'Invalid token address');
      setTokenInfo(null);
      setIsValidAddress(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshTokenBalance = async () => {
    if (!tokenInfo || !publicKey) return;

    try {
      const mintPubkey = new PublicKey(tokenInfo.mint);
      const userPubkey = new PublicKey(publicKey);
      const balanceInfo = await getUserTokenBalance(
        mintPubkey, 
        userPubkey, 
        tokenInfo.decimals,
        tokenInfo.isToken2022 || false
      );
      
      setTokenInfo(prev => prev ? {
        ...prev,
        userBalance: balanceInfo.balance,
        userBalanceFormatted: balanceInfo.formatted
      } : null);
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    }
  };

  const startLiquidityCheck = async (mintPubkey: PublicKey) => {
    setCheckingLiquidity(true);
    
    if (liquidityCheckInterval.current) {
      clearInterval(liquidityCheckInterval.current);
    }

    // Check immediately
    const liquidityData = await checkLiquidity(mintPubkey.toString());
    
    if (liquidityData.hasLiquidity) {
      setTokenInfo(prev => prev ? {
        ...prev,
        hasLiquidity: true,
        liquiditySOL: liquidityData.liquiditySOL,
        pricePerToken: liquidityData.pricePerToken
      } : null);
      
      setCheckingLiquidity(false);
      await checkRecipientAccounts();
    } else {
      // If no liquidity, keep checking every 10 seconds
      liquidityCheckInterval.current = setInterval(async () => {
        try {
          const liquidityData = await checkLiquidity(mintPubkey.toString());
          
          if (liquidityData.hasLiquidity) {
            setTokenInfo(prev => prev ? {
              ...prev,
              hasLiquidity: true,
              liquiditySOL: liquidityData.liquiditySOL,
              pricePerToken: liquidityData.pricePerToken
            } : null);
            
            setCheckingLiquidity(false);
            if (liquidityCheckInterval.current) {
              clearInterval(liquidityCheckInterval.current);
            }
            
            await checkRecipientAccounts();
          }
        } catch (error) {
          console.error('Error checking liquidity:', error);
        }
      }, 10000); // Check every 10 seconds
    }
  };

  const checkRecipientAccounts = async () => {
    if (!tokenInfo) return;

    const mintPubkey = new PublicKey(tokenInfo.mint);
    const programId = tokenInfo.isToken2022 ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
    
    console.log('Checking recipient accounts with program:', programId.toString());
    console.log('Token is Token-2022?', tokenInfo.isToken2022);
    
    setRecipients(prev => prev.map(r => ({ ...r, status: 'checking' as const })));
    
    const updatedRecipients = await Promise.all(
      recipients.map(async (recipient) => {
        if (!recipient.address || recipient.address.length < 32) {
          return { ...recipient, status: 'pending' as const, error: 'Invalid address' };
        }

        try {
          const recipientPubkey = new PublicKey(recipient.address);
          
          // Verify it's a valid Solana address
          const accountInfo = await connection.getAccountInfo(recipientPubkey);
          
          const ata = await getAssociatedTokenAddress(
            mintPubkey, 
            recipientPubkey,
            false,
            programId
          );
          
          try {
            const account = await getAccount(connection, ata, 'confirmed', programId);
            console.log(`Recipient ${recipient.address} has token account with balance: ${account.amount}`);
            return { ...recipient, status: 'ready' as const, hasTokenAccount: true, error: undefined };
          } catch {
            console.log(`Recipient ${recipient.address} needs token account to be created`);
            // Token account doesn't exist, but address is valid
            return { ...recipient, status: 'ready' as const, hasTokenAccount: false, error: undefined };
          }
        } catch (error: any) {
          console.error(`Error checking recipient ${recipient.address}:`, error);
          return { 
            ...recipient, 
            status: 'failed' as const, 
            error: error.message || 'Invalid address'
          };
        }
      })
    );

    setRecipients(updatedRecipients);
  };

  const addRecipient = () => {
    const newId = Date.now().toString();
    setRecipients([...recipients, {
      id: newId,
      address: '',
      amount: 0,
      status: 'pending'
    }]);
  };

  const removeRecipient = (id: string) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter(r => r.id !== id));
    }
  };

 const updateRecipient = (id: string, field: 'address' | 'amount' | 'percentage', value: string | number) => {
  setRecipients(prev => {
    const updated = prev.map(r => {
      if (r.id === id) {
        if (field === 'percentage' && tokenInfo) {
          const percentage = typeof value === 'number' ? value : parseFloat(value) || 0;
          const amount = (tokenInfo.supply * percentage) / 100;
          return { ...r, percentage, amount, status: 'pending' as const };
        } else if (field === 'amount') {
          const amount = typeof value === 'number' ? value : parseFloat(value) || 0;
          const percentage = tokenInfo ? (amount / tokenInfo.supply) * 100 : 0;
          return { ...r, amount, percentage, status: 'pending' as const };
        } else {
          return { ...r, [field]: value, status: 'pending' as const };
        }
      }
      return r;
    });
    
    // Calculate total immediately after update
    const validRecipients = updated.filter(r => r.amount > 0); // Remove address check
    const newTotal = validRecipients.reduce((sum, r) => sum + (r.amount || 0), 0);
    setTotalAmount(newTotal);
    
    return updated;
  });
};

  const handleCSVUpload = async (file: File) => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      const headers = lines[0].toLowerCase();
      const hasPercentage = headers.includes('percentage') || headers.includes('percent');
      const dataLines = headers.includes('address') ? lines.slice(1) : lines;
      
      const newRecipients: AirdropRecipient[] = dataLines.map((line, index) => {
        const parts = line.split(',').map(s => s.trim());
        const address = parts[0] || '';
        
        let amount = 0;
        let percentage = 0;
        
        if (hasPercentage && tokenInfo) {
          // CSV has percentage column
          percentage = parseFloat(parts[1]) || 0;
          amount = (tokenInfo.supply * percentage) / 100;
        } else {
          // CSV has amount column
          amount = parseFloat(parts[1]) || 0;
          percentage = tokenInfo ? (amount / tokenInfo.supply) * 100 : 0;
        }
        
        return {
          id: Date.now().toString() + index,
          address,
          amount,
          percentage,
          status: 'pending' as const
        };
      }).filter(r => r.address && (r.amount > 0 || r.percentage > 0));

      if (newRecipients.length > 0) {
        setRecipients(newRecipients);
        console.log(`Loaded ${newRecipients.length} recipients from CSV`);
        
        // Automatically check accounts after CSV upload
        if (tokenInfo) {
          setTimeout(() => checkRecipientAccounts(), 100);
        }
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert('Failed to parse CSV file. Please ensure format is: address,amount or address,percentage');
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = "address,amount\nWALLET_ADDRESS_1,1000\nWALLET_ADDRESS_2,2000\nWALLET_ADDRESS_3,1500";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'airdrop_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const executeAirdrop = async () => {
    if (!publicKey || !tokenInfo) {
      alert('Please connect wallet and enter token address');
      return;
    }

    const validRecipients = recipients.filter(r => 
      r.address && r.amount > 0 && r.status === 'ready'
    );

    if (validRecipients.length === 0) {
      alert('No valid recipients to airdrop');
      return;
    }

    if (settings.waitForLiquidity && !tokenInfo.hasLiquidity) {
      alert('Waiting for liquidity to be added...');
      return;
    }

    // Check if user has enough balance
    const totalToSend = validRecipients.reduce((sum, r) => sum + r.amount, 0);
    if (tokenInfo.userBalance !== undefined && tokenInfo.userBalance < totalToSend) {
      alert(`Insufficient token balance. You have ${tokenInfo.userBalanceFormatted} tokens but need ${totalToSend.toLocaleString()} tokens.`);
      return;
    }

    setSending(true);
    try {
      const senderPubkey = new PublicKey(publicKey);
      const mintPubkey = new PublicKey(tokenInfo.mint);
      const programId = tokenInfo.isToken2022 ? TOKEN_2022_PROGRAM_ID : TOKEN_PROGRAM_ID;
      
      // Get sender's ATA and verify it exists
      const senderATA = await getAssociatedTokenAddress(mintPubkey, senderPubkey, false, programId);
      
      // Verify sender's token account exists and has balance
      try {
        const senderAccount = await getAccount(connection, senderATA, 'confirmed', programId);
        console.log('Sender token account verified:', {
          address: senderATA.toString(),
          balance: senderAccount.amount.toString(),
          owner: senderAccount.owner.toString(),
          mint: senderAccount.mint.toString()
        });
      } catch (error) {
        console.error('Sender token account not found or incompatible:', error);
        alert(`Your token account doesn't exist or was created with the wrong program. Please ensure you have a ${tokenInfo.isToken2022 ? 'Token-2022' : 'Token'} account for this token.`);
        setSending(false);
        return;
      }

      // Calculate if we can fit all recipients in a single transaction
      const accountsToCreate = validRecipients.filter(r => !r.hasTokenAccount).length;
      const estimatedInstructions = validRecipients.length + accountsToCreate;
      
      // Rule of thumb: ~20-25 transfers fit in one transaction, ~10 with account creations
      const maxPerTransaction = accountsToCreate > 0 ? 10 : 25;
      const needsMultipleTransactions = estimatedInstructions > maxPerTransaction;
      
      if (settings.singleTransaction && !needsMultipleTransactions) {
        // Single transaction for all recipients
        console.log(`Processing all ${validRecipients.length} recipients in a single transaction`);
        
        const transaction = new Transaction();
        
        // Add priority fee only if needed
        if (settings.priorityFee > 0) {
          const microLamportsPerUnit = Math.round((settings.priorityFee * 1_000_000_000) / 200000);
          transaction.add(
            ComputeBudgetProgram.setComputeUnitPrice({
              microLamports: microLamportsPerUnit
            })
          );
        }
        
        // Process all recipients
        for (const recipient of validRecipients) {
          const recipientPubkey = new PublicKey(recipient.address);
          const recipientATA = await getAssociatedTokenAddress(
            mintPubkey, 
            recipientPubkey,
            false,
            programId
          );
          
          // Create token account if needed
          if (!recipient.hasTokenAccount && settings.createTokenAccounts) {
            transaction.add(
              createAssociatedTokenAccountInstruction(
                senderPubkey,
                recipientATA,
                recipientPubkey,
                mintPubkey,
                programId
              )
            );
          }
          
          // Add transfer instruction
          const amountWithDecimals = BigInt(Math.floor(recipient.amount * Math.pow(10, tokenInfo.decimals)));
          transaction.add(
            createTransferInstruction(
              senderATA,
              recipientATA,
              senderPubkey,
              amountWithDecimals,
              [],
              programId
            )
          );
        }
        
        // Get recent blockhash
const { blockhash } = await connection.getLatestBlockhash();
transaction.recentBlockhash = blockhash;
transaction.feePayer = senderPubkey;

// Sign and send using solana service
console.log('Requesting wallet signature for single transaction...');
const signature = await solanaService.signAndSendTransaction(transaction);
setTxSignature(signature);

// Update all recipient statuses
setRecipients(recipients.map(r => 
  validRecipients.find(vr => vr.id === r.id)
    ? { ...r, status: 'sent' as const }
    : r
));

// Refresh token balance
await refreshTokenBalance();

console.log(`✅ Airdrop successful in single transaction! TX: ${signature}`);
alert(`Airdrop successful! All ${validRecipients.length} recipients received tokens in a single transaction.`);
        
        // Update all recipient statuses
        setRecipients(recipients.map(r => 
          validRecipients.find(vr => vr.id === r.id)
            ? { ...r, status: 'sent' as const }
            : r
        ));
        
        // Refresh token balance
        await refreshTokenBalance();
        
        console.log(`✅ Airdrop successful in single transaction! TX: ${signature}`);
        alert(`Airdrop successful! All ${validRecipients.length} recipients received tokens in a single transaction.`);
        
      } else {
        // Multiple transactions needed - use batching
        console.log(`Too many recipients for single transaction. Splitting into batches.`);
        
        const batches = [];
        const batchSize = settings.batchSize || maxPerTransaction;
        for (let i = 0; i < validRecipients.length; i += batchSize) {
          batches.push(validRecipients.slice(i, i + batchSize));
        }
        
        console.log(`Processing ${validRecipients.length} recipients in ${batches.length} batch(es)`);
        
        if (batches.length > 1) {
          const proceed = window.confirm(
            `This will require ${batches.length} separate transactions due to Solana's size limits.\n\n` +
            `Recipients per transaction: ${batchSize}\n` +
            `Total recipients: ${validRecipients.length}\n\n` +
            `Do you want to proceed?`
          );
          
          if (!proceed) {
            setSending(false);
            return;
          }
        }
        
        let successCount = 0;
        const failedRecipients: any[] = [];
        
        // Process each batch
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          console.log(`Processing batch ${batchIndex + 1} of ${batches.length} with ${batch.length} recipients`);
          
          try {
            const transaction = new Transaction();
            
            // Add priority fee only if needed
            if (settings.priorityFee > 0) {
              const microLamportsPerUnit = Math.round((settings.priorityFee * 1_000_000_000) / 200000);
              transaction.add(
                ComputeBudgetProgram.setComputeUnitPrice({
                  microLamports: microLamportsPerUnit
                })
              );
            }
            
            // Process batch recipients
            for (const recipient of batch) {
              const recipientPubkey = new PublicKey(recipient.address);
              const recipientATA = await getAssociatedTokenAddress(
                mintPubkey, 
                recipientPubkey,
                false,
                programId
              );
              
              if (!recipient.hasTokenAccount && settings.createTokenAccounts) {
                transaction.add(
                  createAssociatedTokenAccountInstruction(
                    senderPubkey,
                    recipientATA,
                    recipientPubkey,
                    mintPubkey,
                    programId
                  )
                );
              }
              
              const amountWithDecimals = BigInt(Math.floor(recipient.amount * Math.pow(10, tokenInfo.decimals)));
              transaction.add(
                createTransferInstruction(
                  senderATA,
                  recipientATA,
                  senderPubkey,
                  amountWithDecimals,
                  [],
                  programId
                )
              );
            }
            
            const { blockhash } = await connection.getLatestBlockhash();
            transaction.recentBlockhash = blockhash;
            transaction.feePayer = senderPubkey;
            
            const provider = (window as any).solana;
            const signedTransaction = await provider.signTransaction(transaction);
            const signature = await connection.sendRawTransaction(signedTransaction.serialize());
            await connection.confirmTransaction(signature, 'confirmed');
            
            console.log(`✅ Batch ${batchIndex + 1} successful! TX: ${signature}`);
            
            const batchIds = batch.map(r => r.id);
            setRecipients(prev => prev.map(r => 
              batchIds.includes(r.id) ? { ...r, status: 'sent' as const } : r
            ));
            
            successCount += batch.length;
            
            if (batchIndex === batches.length - 1) {
              setTxSignature(signature);
            }
            
            if (batchIndex < batches.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
          } catch (error: any) {
            console.error(`Batch ${batchIndex + 1} failed:`, error);
            failedRecipients.push(...batch);
            
            if (batchIndex < batches.length - 1) {
              const continueProcessing = window.confirm(
                `Batch ${batchIndex + 1} failed. Do you want to continue with the remaining ${batches.length - batchIndex - 1} batch(es)?`
              );
              if (!continueProcessing) {
                break;
              }
            }
          }
        }
        
        await refreshTokenBalance();
        
        if (successCount > 0) {
          alert(`Airdrop completed! ${successCount} recipients received tokens.${
            failedRecipients.length > 0 ? ` ${failedRecipients.length} recipients failed.` : ''
          }`);
        } else {
          alert('Airdrop failed for all recipients.');
        }
      }
      
    } catch (error: any) {
      console.error('Airdrop failed:', error);
      alert(`Airdrop failed: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status: AirdropRecipient['status']) => {
    switch (status) {
      case 'ready': return <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />;
      case 'sent': return <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />;
      case 'failed': return <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />;
      case 'checking': return <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin text-yellow-400" />;
      default: return <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-700">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="p-1.5 sm:p-2 bg-purple-600/20 rounded-lg">
            <Send className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Token Airdrop</h2>
            <p className="text-xs sm:text-sm text-gray-400">Distribute tokens efficiently</p>
          </div>
        </div>
        
        {tokenInfo?.hasLiquidity && (
          <div className="text-left sm:text-right">
            <div className="text-xs text-gray-500">Token Has Market</div>
            <div className="text-base sm:text-lg font-semibold text-green-400">
              ${tokenInfo.pricePerToken?.toFixed(8) || '0'}
            </div>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs sm:text-sm">
            <div className="font-medium text-blue-400 mb-1">How It Works</div>
            <ul className="text-xs text-gray-400 space-y-0.5 sm:space-y-1">
              <li>1. Enter token contract address</li>
              <li>2. Add recipient wallets</li>
              <li>3. Check accounts</li>
              <li>4. Execute airdrop</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Token Contract Address */}
        <div>
          <label className="block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2">
            Token Contract Address <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={tokenCA}
                onChange={(e) => setTokenCA(e.target.value)}
                className={`w-full px-3 sm:px-4 py-2 text-sm bg-gray-900/50 border rounded-lg focus:outline-none focus:border-purple-500 transition-colors pr-10 ${
                  addressError ? 'border-red-500' : isValidAddress ? 'border-green-500' : 'border-gray-700'
                }`}
                placeholder="Enter token mint address..."
              />
              {loading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                </div>
              )}
              {!loading && isValidAddress && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
              )}
            </div>
            <button
              onClick={() => validateAndLoadToken()}
              disabled={loading || !tokenCA}
              className="px-4 sm:px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reload</span>
            </button>
          </div>
          
          {addressError && (
            <div className="mt-2 text-xs sm:text-sm text-red-400">{addressError}</div>
          )}
          
          {checkingLiquidity && (
            <div className="mt-2 flex items-center space-x-2 text-xs sm:text-sm text-yellow-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Checking for liquidity...</span>
            </div>
          )}
          
          {tokenInfo && (
            <div className="mt-3 space-y-3">
              {/* Token Info Card */}
              <div className="p-3 sm:p-4 bg-gray-900/50 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
                  {tokenInfo.logoURI && (
                    <img 
                      src={tokenInfo.logoURI} 
                      alt={tokenInfo.symbol}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <span className="font-semibold text-base sm:text-lg">{tokenInfo.name}</span>
                      <span className="text-xs sm:text-sm text-gray-400">({tokenInfo.symbol})</span>
                      {tokenInfo.isToken2022 && (
                        <span className="px-2 py-0.5 bg-purple-600/20 text-purple-400 text-xs rounded-full">
                          Token-2022
                        </span>
                      )}
                    </div>
                    {tokenInfo.description && (
                      <p className="text-xs text-gray-500 mb-2">{tokenInfo.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                      <div>
                        <div className="text-xs text-gray-500">Decimals</div>
                        <div className="font-mono">{tokenInfo.decimals}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Supply</div>
                        <div className="font-mono truncate">{tokenInfo.supply.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Market Status</div>
                        <div className={tokenInfo.hasLiquidity ? "text-green-400" : "text-yellow-400"}>
                          {tokenInfo.hasLiquidity ? 'Active' : 'No Market'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Price</div>
                        <div className="font-mono truncate">
                          {tokenInfo.pricePerToken ? `$${tokenInfo.pricePerToken.toFixed(8)}` : '-'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-2">
                      <button
                        onClick={() => copyToClipboard(tokenInfo.mint)}
                        className="text-xs text-gray-400 hover:text-purple-400 flex items-center space-x-1"
                      >
                        <Copy className="w-3 h-3" />
                        <span>{tokenInfo.mint.slice(0, 4)}...{tokenInfo.mint.slice(-4)}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Balance Card */}
              {isConnected && (
                <div className="p-3 sm:p-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-lg border border-purple-500/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <div className="flex items-center space-x-2">
                      <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                      <span className="text-xs sm:text-sm font-medium">Your Balance</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg sm:text-xl font-bold text-purple-400">
                        {tokenInfo.userBalanceFormatted || '0'}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-400">{tokenInfo.symbol}</span>
                      <button
                        onClick={refreshTokenBalance}
                        className="p-1 hover:bg-purple-600/20 rounded transition-colors"
                        title="Refresh balance"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {tokenInfo.userBalance !== undefined && totalAmount > 0 && (
                    <div className="mt-2 text-xs">
                      {tokenInfo.userBalance >= totalAmount ? (
                        <span className="text-green-400">
                          ✓ Sufficient balance ({totalAmount.toLocaleString()} needed)
                        </span>
                      ) : (
                        <span className="text-red-400">
                          ✗ Insufficient (need {(totalAmount - tokenInfo.userBalance).toLocaleString()} more)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* CSV Upload/Download */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-0 sm:justify-between p-3 sm:p-4 bg-gray-900/30 rounded-lg">
          <div className="flex items-center justify-between sm:justify-start space-x-2 sm:space-x-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center space-x-1 sm:space-x-2"
            >
              <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Upload CSV</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleCSVUpload(file);
              }}
            />
            
            <button
              onClick={downloadCSVTemplate}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center space-x-1 sm:space-x-2"
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Template</span>
            </button>
          </div>
          
          <button
            onClick={addRecipient}
            className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center justify-center space-x-1 sm:space-x-2"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>Add Recipient</span>
          </button>
        </div>

        {/* Recipients List */}
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
            <label className="text-xs sm:text-sm font-medium flex items-center space-x-1 sm:space-x-2">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
              <span>Recipients ({recipients.length})</span>
            </label>
            <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-4">
              {/* Input Mode Toggle */}
              <div className="flex items-center bg-gray-900/50 rounded-lg p-1">
                <button
                  onClick={() => setInputMode('amount')}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded transition-colors ${
                    inputMode === 'amount' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Amount
                </button>
                <button
                  onClick={() => setInputMode('percentage')}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded transition-colors ${
                    inputMode === 'percentage' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                  disabled={!tokenInfo}
                  title={!tokenInfo ? 'Load token first to use percentage mode' : ''}
                >
                  % Supply
                </button>
              </div>
              <div className="text-xs sm:text-sm text-gray-400">
                <span className="hidden sm:inline">Total: </span>
                <span className="font-mono">{totalAmount.toLocaleString()}</span>
                <span className="hidden sm:inline"> {tokenInfo?.symbol || 'tokens'}</span>
              </div>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto space-y-2">
            {recipients.map((recipient, index) => (
              <div key={recipient.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 p-2 sm:p-0 bg-gray-900/30 sm:bg-transparent rounded-lg">
                <span className="text-xs sm:text-sm text-gray-500 w-auto sm:w-8">#{index + 1}</span>
                
                <input
                  type="text"
                  value={recipient.address}
                  onChange={(e) => updateRecipient(recipient.id, 'address', e.target.value)}
                  className="flex-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                  placeholder="Wallet address"
                />
                
                <div className="flex items-center space-x-2">
                  {inputMode === 'amount' ? (
                    <>
                      <input
                        type="number"
                        value={recipient.amount || ''}
                        onChange={(e) => updateRecipient(recipient.id, 'amount', e.target.value)}
                        className="w-24 sm:w-32 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                        placeholder="Amount"
                        min="0"
                        step="0.01"
                      />
                      {tokenInfo && recipient.amount > 0 && (
                        <span className="text-xs text-gray-500 hidden sm:inline-block w-16">
                          {((recipient.amount / tokenInfo.supply) * 100).toFixed(2)}%
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="relative">
                        <input
                          type="number"
                          value={recipient.percentage || ''}
                          onChange={(e) => updateRecipient(recipient.id, 'percentage', e.target.value)}
                          className="w-20 sm:w-24 px-2 sm:px-3 py-1.5 sm:py-2 pr-6 sm:pr-8 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                          placeholder="%"
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={!tokenInfo}
                        />
                        <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs sm:text-sm">%</span>
                      </div>
                      {tokenInfo && recipient.percentage && recipient.percentage > 0 && (
                        <span className="text-xs text-gray-500 hidden sm:inline-block w-32">
                          = {recipient.amount.toLocaleString()} {tokenInfo.symbol}
                        </span>
                      )}
                    </>
                  )}
                  
                  {getStatusIcon(recipient.status)}
                  
                  <button
                    onClick={() => removeRecipient(recipient.id)}
                    disabled={recipients.length === 1}
                    className="p-1.5 sm:p-2 text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Quick Percentage Buttons */}
          {inputMode === 'percentage' && tokenInfo && (
            <div className="mt-3 p-2 sm:p-3 bg-gray-900/30 rounded-lg">
              <div className="text-xs text-gray-400 mb-2">Quick Fill (% per recipient):</div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {[0.1, 0.5, 1, 2, 5, 10].map(percent => (
                  <button
                    key={percent}
                    onClick={() => {
                      setRecipients(recipients.map(r => ({
                        ...r,
                        percentage: percent,
                        amount: (tokenInfo.supply * percent) / 100,
                        status: 'pending' as const
                      })));
                    }}
                    className="px-2 sm:px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs font-medium transition-colors"
                  >
                    {percent}%
                  </button>
                ))}
                <button
                  onClick={() => {
                    // Distribute evenly among recipients
                    const percentPerRecipient = 100 / recipients.length;
                    setRecipients(recipients.map(r => ({
                      ...r,
                      percentage: percentPerRecipient,
                      amount: (tokenInfo.supply * percentPerRecipient) / 100,
                      status: 'pending' as const
                    })));
                  }}
                  className="px-2 sm:px-3 py-1 bg-purple-600/30 hover:bg-purple-600/40 border border-purple-500/50 rounded text-xs font-medium transition-colors"
                >
                  Distribute Evenly
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Settings */}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="text-sm sm:text-base font-medium flex items-center space-x-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Advanced Settings</span>
            </span>
            <span className="text-gray-400">
              {showAdvanced ? '−' : '+'}
            </span>
          </button>
          
          {showAdvanced && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={settings.waitForLiquidity}
                      onChange={(e) => setSettings({ ...settings, waitForLiquidity: e.target.checked })}
                      className="w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                    />
                    <span className="text-xs sm:text-sm">Wait for Liquidity</span>
                  </label>
                  {settings.waitForLiquidity && (
                    <div className="text-xs text-gray-500">
                      Will wait for token to have an active market
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={settings.createTokenAccounts}
                      onChange={(e) => setSettings({ ...settings, createTokenAccounts: e.target.checked })}
                      className="w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                    />
                    <span className="text-xs sm:text-sm">Auto-create Token Accounts</span>
                  </label>
                  <div className="text-xs text-gray-500">
                    Creates accounts (~0.002 SOL each)
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">Batch Size</label>
                  <input
                    type="number"
                    value={settings.batchSize}
                    onChange={(e) => setSettings({ ...settings, batchSize: Math.max(1, Math.min(30, parseInt(e.target.value) || 20)) })}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                    min="1"
                    max="30"
                  />
                  <div className="text-xs text-gray-500 mt-1">Recipients per TX</div>
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">Priority Fee (SOL)</label>
                  <input
                    type="number"
                    value={settings.priorityFee}
                    onChange={(e) => setSettings({ ...settings, priorityFee: parseFloat(e.target.value) || 0 })}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                    min="0"
                    step="0.0001"
                  />
                </div>
                
                <div>
                  <label className="flex items-center space-x-2 mb-1 sm:mb-2">
                    <input
                      type="checkbox"
                      checked={settings.useJitoBundle}
                      onChange={(e) => setSettings({ ...settings, useJitoBundle: e.target.checked })}
                      className="w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                    />
                    <span className="text-xs sm:text-sm">Use Jito Bundle</span>
                  </label>
                  {settings.useJitoBundle && (
                    <input
                      type="number"
                      value={settings.jitoTip}
                      onChange={(e) => setSettings({ ...settings, jitoTip: parseFloat(e.target.value) || 0.01 })}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-purple-500"
                      placeholder="Jito tip (SOL)"
                      min="0.001"
                      step="0.001"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-900/50 rounded-lg p-3 sm:p-4">
          <div className="flex items-start space-x-2">
            <DollarSign className="w-4 h-4 text-purple-400 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-purple-400 mb-2 sm:mb-3">Airdrop Summary</div>
              <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Recipients:</span>
                  <span className="font-mono">{recipients.filter(r => r.address && r.amount > 0).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Tokens:</span>
                  <span className="font-mono truncate ml-2">{totalAmount.toLocaleString()} {tokenInfo?.symbol || ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Accounts to Create:</span>
                  <span className="font-mono">{recipients.filter(r => r.address && !r.hasTokenAccount).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Est. TX Fee:</span>
                  <span className="font-mono">{estimatedCost.toFixed(6)} SOL</span>
                </div>
                {tokenInfo?.pricePerToken && tokenInfo.pricePerToken > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total Value:</span>
                    <span className="font-mono text-green-400">
                      ${(totalAmount * tokenInfo.pricePerToken).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <button
            onClick={checkRecipientAccounts}
            disabled={!tokenInfo || recipients.length === 0 || loading}
            className="flex-1 py-2.5 sm:py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Check Accounts</span>
          </button>
          
          <button
            onClick={executeAirdrop}
            disabled={
              !isConnected || 
              !tokenInfo || 
              recipients.filter(r => r.status === 'ready').length === 0 ||
              (settings.waitForLiquidity && !tokenInfo.hasLiquidity) ||
              (tokenInfo.userBalance !== undefined && tokenInfo.userBalance < totalAmount) ||
              sending
            }
            className="flex-1 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Execute Airdrop</span>
              </>
            )}
          </button>
        </div>

        {/* Transaction Result */}
        {txSignature && (
          <div className="p-3 sm:p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-green-400 mb-1">Airdrop Successful!</div>
                <div className="text-xs sm:text-sm text-gray-400">
                  <span className="block sm:inline">Transaction:</span>
                  <a
                    href={`https://solscan.io/tx/${txSignature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-0 sm:ml-2 text-purple-400 hover:text-purple-300 font-mono text-xs break-all"
                  >
                    {txSignature.slice(0, 8)}...{txSignature.slice(-8)}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isConnected && (
          <div className="text-center text-xs sm:text-sm text-yellow-400 flex items-center justify-center space-x-2">
            <AlertCircle className="w-4 h-4" />
            <span>Please connect your wallet to use the airdrop feature</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Airdrop;